import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, MousePointer2, Loader2, Image as ImageIcon, FileText,
  Sparkles,
} from "lucide-react";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";

const TYPEFORM_URL = "https://form.typeform.com/to/yXF9HCS0";

/* ---------- Reusable bits ---------- */

function LivePill({ children, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-full border border-[#1f1f22] bg-gradient-to-r from-white/95 to-[#d8ff2b]/85 px-4 py-2 text-[13px] font-semibold tracking-wide text-black shadow-[0_0_30px_rgba(216,255,43,0.15)]"
    >
      <span className="cb-live-dot" />
      {children}
    </span>
  );
}

function SectionLabel({ index, title }) {
  return (
    <div className="mb-3 flex items-baseline gap-3">
      <span className="cb-display text-3xl text-white sm:text-4xl">0{index}</span>
      <span className="text-xs font-bold uppercase tracking-[0.22em] text-zinc-400">{title}</span>
    </div>
  );
}

/* ---------- Step 1 — animated Sign Up form ---------- */

function Step1SignUp() {
  return (
    <div data-testid="cb-step-1" className="cb-card cb-card-hover relative overflow-hidden p-6 sm:p-8">
      <div className="grid gap-6 lg:grid-cols-[1.1fr,1fr] lg:items-center">
        {/* Left: copy */}
        <div>
          <SectionLabel index={1} title="Sign Up" />
          <h3 className="cb-display text-3xl text-white sm:text-4xl">Create your account</h3>
          <p className="mt-3 max-w-md text-sm text-[#8a8a90]">
            Create your account and unlock your dashboard to start building and tracking campaigns in real time.
          </p>
        </div>

        {/* Right: mock form with cursor */}
        <div className="relative rounded-2xl border border-[#1f1f22] bg-black/60 p-5">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.18em] text-zinc-500">Email</span>
              <div className="rounded-xl border border-[#1f1f22] bg-[#0a0a0c] px-3.5 py-2.5 text-sm text-zinc-300">example@mail.com</div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.18em] text-zinc-500">Industry</span>
              <div className="flex items-center justify-between rounded-xl border border-[#1f1f22] bg-[#0a0a0c] px-3.5 py-2.5 text-sm text-zinc-300">
                <span>Brand / E-commerce / Podcast</span>
                <ArrowRight size={14} className="text-zinc-500" />
              </div>
            </label>

            {/* Button + cursor (cursor lands on RIGHT edge of button) */}
            <div className="relative pt-1">
              <button
                disabled
                data-testid="cb-step-1-signup-btn"
                className="cb-btn-press relative inline-flex w-full items-center justify-center rounded-xl bg-[#d8ff2b] px-4 py-3 text-sm font-bold text-black"
              >
                <span className="cb-label">Sign Up</span>
                <Loader2 size={18} className="cb-loader cb-spin pointer-events-none absolute left-1/2 top-1/2" />
              </button>
              {/* Cursor anchored to the button's right edge */}
              <div className="pointer-events-none absolute z-10" style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }}>
                <div className="cb-cursor text-white drop-shadow-[0_4px_10px_rgba(0,0,0,0.7)]">
                  <MousePointer2 size={22} strokeWidth={1.5} fill="#ffffff" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 2 — Create a Campaign ---------- */

function Step2Campaign() {
  const chips = [
    { label: "Clipping", soon: false },
    { label: "Logo", soon: false },
    { label: "Music", soon: false },
    { label: "UGC", soon: true },
  ];
  return (
    <div data-testid="cb-step-2" className="cb-card cb-card-hover p-6 sm:p-8">
      <div className="grid gap-6 lg:grid-cols-[1fr,1.15fr] lg:items-center">
        <div>
          <SectionLabel index={2} title="Create a Campaign" />
          <h3 className="cb-display text-3xl text-white sm:text-4xl">Choose your goals</h3>
          <p className="mt-3 max-w-md text-sm text-[#8a8a90]">
            Set up your campaign by choosing your goals, audience, and content type to maximize performance across creators.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">Content Type</div>
            <div className="flex flex-wrap gap-2">
              {chips.map((c) => (
                <span
                  key={c.label}
                  data-testid={`cb-step-2-chip-${c.label.toLowerCase()}`}
                  className="relative inline-flex items-center gap-1.5 rounded-full border border-[#1f1f22] bg-[#0a0a0c] px-3 py-1.5 text-xs font-semibold text-zinc-200"
                >
                  {c.label}
                  {c.soon && (
                    <span className="ml-1 rounded-full bg-[#d8ff2b] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-black">SOON</span>
                  )}
                </span>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-[#1f1f22] bg-[#0a0a0c] p-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#d8ff2b]/10 text-[#d8ff2b]">
                  <ImageIcon size={18} />
                </span>
                <div className="text-sm font-bold text-white">Upload Assets</div>
              </div>
              <p className="mt-2.5 text-xs text-[#8a8a90]">Videos, images, or links.</p>
            </div>

            <div className="rounded-2xl border border-[#1f1f22] bg-[#0a0a0c] p-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#d8ff2b]/10 text-[#d8ff2b]">
                  <FileText size={18} />
                </span>
                <div className="text-sm font-bold text-white">Requirements</div>
              </div>
              <p className="mt-2.5 text-xs text-[#8a8a90]">Tags, guidelines, instructions.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 3 — Budget & CPM ---------- */

function Step3Budget() {
  const rows = [
    { label: "Total Budget", value: "$1,000", accent: true },
    { label: "CPM Rate", value: "$0.50", accent: false },
    { label: "Max Cap", value: "$60 / post", accent: false },
  ];
  return (
    <div data-testid="cb-step-3" className="cb-card cb-card-hover p-6 sm:p-8">
      <div className="grid gap-6 lg:grid-cols-[1fr,1fr] lg:items-center">
        <div>
          <SectionLabel index={3} title="Set Budget & CPM" />
          <h3 className="cb-display text-3xl text-white sm:text-4xl">You control the spend</h3>
          <p className="mt-3 max-w-md text-sm text-[#8a8a90]">
            Control your spending by setting a budget and choosing your CPM to balance cost with performance.
          </p>
        </div>

        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.label}
              data-testid={`cb-step-3-row-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center justify-between rounded-2xl border border-[#1f1f22] bg-[#0a0a0c] px-4 py-3.5"
            >
              <span className="text-sm text-zinc-300">{r.label}</span>
              <span className={`cb-display text-lg ${r.accent ? "text-[#d8ff2b]" : "text-white"}`}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 4 — Launch & Get Reach ---------- */

function Step4Launch() {
  return (
    <div data-testid="cb-step-4" className="cb-card cb-card-hover p-6 sm:p-8">
      <div className="grid gap-6 lg:grid-cols-[1fr,1.2fr] lg:items-center">
        <div>
          <SectionLabel index={4} title="Launch & Get Reach" />
          <h3 className="cb-display text-3xl text-white sm:text-4xl">Go live, grow fast</h3>
          <p className="mt-3 max-w-md text-sm text-[#8a8a90]">
            Once live, creators start posting your content across platforms — generating views, engagement, and conversions instantly.
          </p>
        </div>

        <div className="relative h-48 overflow-hidden rounded-2xl border border-[#1f1f22] bg-[#08080a]">
          {/* Edge-faded, very-faded grid */}
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
              WebkitMaskImage: "linear-gradient(to right, transparent 0, #000 22%, #000 78%, transparent 100%)",
              maskImage: "linear-gradient(to right, transparent 0, #000 22%, #000 78%, transparent 100%)",
            }}
          />

          <svg viewBox="0 0 600 240" className="relative h-full w-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="cb-line" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="70%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#d8ff2b" />
              </linearGradient>
              <linearGradient id="cb-area" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#d8ff2b" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#d8ff2b" stopOpacity="0" />
              </linearGradient>
            </defs>

            <path
              className="cb-draw-area"
              d="M0,200 C90,190 160,170 230,160 C300,150 360,130 420,100 C470,76 520,52 560,40 L560,240 L0,240 Z"
              fill="url(#cb-area)"
            />
            <path
              className="cb-draw-line"
              d="M0,200 C90,190 160,170 230,160 C300,150 360,130 420,100 C470,76 520,52 560,40"
              fill="none"
              stroke="url(#cb-line)"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            {/* Pulsing dot positioned before the peak (~5 grid spaces back) */}
            <circle cx="455" cy="83" r="5" fill="#d8ff2b" className="cb-dot-blink" />
          </svg>

          <div className="absolute left-4 top-4 text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400">
            Real Results · Growth Over Time
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Logo Slider ---------- */

function TikTokIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.95a8.16 8.16 0 0 0 4.77 1.52V7.03a4.85 4.85 0 0 1-1.84-.34z" />
    </svg>
  );
}
function YouTubeIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M23.498 6.186a2.999 2.999 0 0 0-2.112-2.124C19.557 3.5 12 3.5 12 3.5s-7.557 0-9.386.562A2.999 2.999 0 0 0 .502 6.186 31.46 31.46 0 0 0 0 12a31.46 31.46 0 0 0 .502 5.814 2.999 2.999 0 0 0 2.112 2.124C4.443 20.5 12 20.5 12 20.5s7.557 0 9.386-.562a2.999 2.999 0 0 0 2.112-2.124A31.46 31.46 0 0 0 24 12a31.46 31.46 0 0 0-.502-5.814ZM9.75 15.568V8.432L15.818 12 9.75 15.568Z" />
    </svg>
  );
}
function InstagramSquareIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}
function XSquareIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2H21l-6.49 7.41L22 22h-6.75l-4.69-6.04L4.96 22H2.2l6.97-7.95L2 2h6.91l4.24 5.5L18.244 2Zm-1.18 18h1.86L7.05 4H5.1l11.964 16Z" />
    </svg>
  );
}

function LogoSlider() {
  const items = [
    { label: "TikTok", Icon: TikTokIcon, soon: false },
    { label: "YouTube", Icon: YouTubeIcon, soon: false },
    { label: "Instagram", Icon: InstagramSquareIcon, soon: false },
    { label: "X", Icon: XSquareIcon, soon: true },
  ];
  const loop = [...items, ...items, ...items, ...items];
  return (
    <section data-testid="cb-logo-slider" className="bg-[#050505]">
      <div className="mx-auto max-w-xl px-5 py-10 sm:px-8">
        <div className="cb-marquee-mask-tight overflow-hidden rounded-2xl border border-[#1f1f22] bg-[#08080a]/80 py-4">
          <div className="cb-marquee-track cb-marquee-40">
            {loop.map((it, i) => (
              <div
                key={`${it.label}-${i}`}
                className="mx-7 inline-flex items-center gap-2 text-[18px] font-semibold text-zinc-300"
              >
                <it.Icon size={18} />
                <span>{it.label}</span>
                {it.soon && (
                  <span className="ml-1 rounded-full bg-[#d8ff2b] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-black">SOON</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */

export function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="cb-root">
      <MarketingNav />

      {/* Hero */}
      <section className="cb-hero-bg relative flex min-h-[calc(100vh-4rem)] items-center">
        <div className="mx-auto w-full max-w-5xl px-5 py-16 text-center sm:px-8">
          <div className="cb-fade-up flex justify-center" style={{ animationDelay: "0s" }}>
            <LivePill testId="cb-hero-live-pill">
              <Sparkles size={14} />
              Creator-Powered Distribution
            </LivePill>
          </div>

          <h1
            data-testid="cb-hero-heading"
            className="cb-display cb-fade-up mx-auto mt-7 max-w-5xl text-5xl leading-[1.02] text-white sm:text-7xl lg:text-[88px]"
            style={{ animationDelay: "0.1s" }}
          >
            The Platform for <span className="text-[#d8ff2b]">Viral</span> Content &{" "}
            <span className="cb-glow-lime text-[#d8ff2b]">Clipping Distribution</span>
          </h1>

          <p
            data-testid="cb-hero-subtitle"
            className="cb-fade-up mx-auto mt-7 max-w-3xl text-lg text-[#8a8a90] sm:text-xl"
            style={{ animationDelay: "0.2s" }}
          >
            ClippingBase helps brands distribute short-form content at scale through a trusted creator network, delivering reach at a lower cost than traditional ads.
          </p>

          <div className="cb-fade-up mt-10 flex flex-wrap items-center justify-center gap-3.5" style={{ animationDelay: "0.3s" }}>
            <a
              href={TYPEFORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cb-hero-launch-btn"
              className="cb-btn-hover cb-cta cb-cta-equal group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-7 py-4 text-base font-bold text-black hover:bg-zinc-100"
            >
              Launch a Campaign
              <ArrowRight size={18} className="cb-arrow" />
            </a>
            <button
              onClick={() => navigate("/creator")}
              data-testid="cb-hero-clip-btn"
              className="cb-btn-hover cb-cta cb-cta-equal group inline-flex items-center justify-center gap-2 rounded-2xl bg-[#d8ff2b] px-7 py-4 text-base font-bold text-black hover:bg-[#c5ee1a]"
            >
              Start Clipping
              <ArrowRight size={18} className="cb-arrow" />
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#050505]">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
          <div className="mb-12">
            <h2 className="cb-display max-w-3xl text-5xl text-white sm:text-7xl">How It Works.</h2>
          </div>

          <div className="space-y-5">
            <Step1SignUp />
            <Step2Campaign />
            <Step3Budget />
            <Step4Launch />
          </div>
        </div>
      </section>

      <LogoSlider />

      <MarketingFooter />
    </div>
  );
}
